<template>
    <v-tooltip
        :disabled="!isTruncated"
        bottom
        max-width="420"
        content-class="middle-ellipsis-text__tooltip"
    >
        <template #activator="{ on, attrs }">
            <span
                ref="label"
                class="middle-ellipsis-text"
                v-bind="attrs"
                v-on="on"
            >{{ displayText }}</span>
        </template>
        <span>{{ text }}</span>
    </v-tooltip>
</template>

<script>
/**
 * Truncates long labels in the middle (start…end) when the container is too
 * narrow. Shows the full text in a tooltip only when truncated.
 */
export default {
    name: 'MiddleEllipsisText',
    props: {
        text: {
            type: [String, Number],
            default: '',
        },
    },
    data() {
        return {
            displayText: '',
            isTruncated: false,
            resizeObserver: null,
            recomputeRaf: null,
        };
    },
    watch: {
        text: {
            immediate: true,
            handler() {
                this.scheduleRecompute();
            },
        },
    },
    mounted() {
        this.recompute();
        if (typeof ResizeObserver !== 'undefined' && this.$refs.label) {
            // Defer to the next frame: sync DOM writes inside ResizeObserver
            // callbacks trigger "loop completed with undelivered notifications"
            // (harmless in prod, noisy in webpack-dev-server overlay).
            this.resizeObserver = new ResizeObserver(() => {
                this.scheduleRecompute();
            });
            this.resizeObserver.observe(this.$refs.label);
        }
    },
    beforeDestroy() {
        if (this.recomputeRaf !== null) {
            cancelAnimationFrame(this.recomputeRaf);
            this.recomputeRaf = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    },
    methods: {
        fullText() {
            if (this.text === null || this.text === undefined) {
                return '';
            }
            return String(this.text);
        },
        buildMiddle(source, visibleChars) {
            if (visibleChars <= 0) {
                return '…';
            }
            if (visibleChars >= source.length) {
                return source;
            }
            const front = Math.ceil(visibleChars / 2);
            const back = Math.floor(visibleChars / 2);
            return `${source.slice(0, front)}…${source.slice(source.length - back)}`;
        },
        fits(el) {
            return el.scrollWidth <= el.clientWidth + 1;
        },
        scheduleRecompute() {
            if (this.recomputeRaf !== null) {
                cancelAnimationFrame(this.recomputeRaf);
            }
            this.recomputeRaf = requestAnimationFrame(() => {
                this.recomputeRaf = null;
                this.recompute();
            });
        },
        recompute() {
            const el = this.$refs.label;
            const source = this.fullText();
            if (!el) {
                this.displayText = source;
                this.isTruncated = false;
                return;
            }
            // Measure against the live DOM node (avoid waiting on Vue updates).
            el.textContent = source;
            if (!source || this.fits(el)) {
                this.displayText = source;
                this.isTruncated = false;
                return;
            }
            let lo = 0;
            let hi = source.length;
            let best = '…';
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                const candidate = this.buildMiddle(source, mid);
                el.textContent = candidate;
                if (this.fits(el)) {
                    best = candidate;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            this.displayText = best;
            this.isTruncated = best !== source;
            el.textContent = best;
        },
    },
};
</script>

<style lang="scss" scoped>
.middle-ellipsis-text {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
}
</style>
